import _ from 'lodash';
import { ReactNode, Component, Fragment, lazy, Suspense } from 'react';
import { Button, Panel } from 'rsuite';
import { toast, ToastContainer, ToastContentProps, Id as ToastId } from "react-toastify";

import { loadDomainURL, YodiBoardToYodiboardExt } from './BoardUtils';
import {
    YodiBoardConfiguration,
    DashboardStructs,
    WebApps,
    DashboardConfiguration,
    YodiBoardExt,
    DashboardViewWidgets,
    DashboardMaintenance,
    YodiBoardWSPolicy
} from '@ModelViews/ModelViews';

import Factory from '@UI/Widgets/Factory';
import { WSStatus } from '@UI/Dashboard/BoardWSStatus';
import { BoardThemeColorOverride, BoardTheming, GetDefaultBoardTheme } from "@UI/Dashboard/BoardTheming";
import { DashboardManager, DashboardTheme } from '@UI/Dashboard/DashboardManager';
import { MacroInfoEx, MacroSettingsUnpack } from '@UI/Dashboard/BoardMacroEngine';
import { DashboardDescriptor, DashboardDescriptorDictionary, DashboardHelper } from '@UI/Dashboard/DashboardStructs';
import { IWidgetClass, IWidgetDescription, IWidgetDictionary, IWidgetGroupDesc } from '@UI/Widgets/WidgetStructs';
import { WebNode } from '@API/WebNode';
import { WebModule } from '@API/WebModule';
import { PlegmaStatics } from '@API/PlegmaStatics';
import { BoardThings } from '@UI/Dashboard/BoardThings';
import { Plegma } from '@API/Api';
import { ErrorCode, WSConf } from '@API/WS';
import { YFetch } from '@Utils/YFetch';
import { inDebug } from '@Utils/Utils';
import { DebugEx } from '@Utils/DebugEx';
import { ServerPaths } from "@Utils/Paths";
import { getGlobal, setGlobal } from '@Utils/Globals';
import { isDefined, isFunction, IsNullOrWhiteSpace, isStringEmpty, RunSafe } from '@Utils/Micro';
import { showToastNotification } from '@Primitive/ToastNotification';
import { IconButton } from '@Primitive/Button';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import { DialogConf, ExDialog } from '@Primitive/ExDialog';
import { ToolbarIconInfo } from '@Primitive/ToolbarIconButton';
import { Localization, LocalizationDefaultKey, LocalizationDictionary } from '@Primitive/Localization/Localization';
import { BoardLoader } from "./BoardLoader";
import { getZDepthShadow } from '@Utils/Colors';

import "./Board.less";
import { Props } from '@Utils/ReactProps';

const Maintenance = lazy(() => import('./Maintenance'));
// ================================================================================================
// Board component
// ================================================================================================
export interface BoardProp extends Props<Board> {
    width: number;
    height: number;

    LoadingYodiBoardId: string
    LoadingPublicBoard: DashboardStructs.YodiBoardPublicSharingInfo;
    IsEditable: boolean;
    ServersInfo: { [key: number]: WebApps.CyanServerInfo };
    UserInfo: WebApps.UserInfoView;
    DomainInfo: WebApps.DomainInfo;
    Theme: DashboardTheme;
    SelectedPageId: string;
    DefaultIsListOpen?: boolean;
    DefaultIsListEnabled?: boolean;

    onSelectedBoardOrPageChange?: (boardId: string, pageId: string) => void;
    onPagesLoad?: (yodiBoardId: string, pages: DashboardStructs.YodiPage[]) => void;
    onAssignedEndpoint?: (key: string) => void;
    onError?: (msg: string) => void;
    onUnauthorizedRequest?: (loginPath: string) => void;

    getToolbarElements?: (nodeKey: string) => {
        LeftElements?: ReactNode[];
        CenterElements?: ReactNode[];
        RightElements?: ReactNode[];
    }
}

export enum ErrorStatus {
    NoError,
    NetworkProblem,
    ServerProblem,
    NoAccessRights
}

export interface BoardState {
    isDashboardSaving: boolean;
    isDashboardLoading: boolean;
    isDashboardSavingFailed: boolean;
    selectedPageId: string;
    LoadedYodiBoard?: YodiBoardExt;
    widgetGroups: IWidgetGroupDesc[];
    pages: DashboardDescriptorDictionary;
    DialogModal?: DialogConf;
    ErrorStatus: ErrorStatus;
    showCookiesConsent: boolean;
}
// --------------------------------------------------------------------------------------------------
export function GetDefaultDashboardTheme() {
    if (!IsNullOrWhiteSpace((window as any).LoadingImageURL))
        return {
            Loading: {
                LoadingImageURL: (window as any).LoadingImageURL,
                TextColor: "rgb(19, 68, 89)",
                NoPoweredByText: (window as any).NoPoweredByText == 'True' || (window as any).NoPoweredByText == true
            }
        };
    else
        return {
            Loading: {
                LoadingImageURL: "/Content/img/Emerald-icon.png",
                TextColor: "rgb(19, 68, 89)",
                NoPoweredByText: false
            }
        };
}
// --------------------------------------------------------------------------------------------------
const AutoUpdateInterval = 10 * 60 * 1000; // 10 min
const UpdateTimeoutOnEdit = 2 * 60 * 1000; // 2 min
const UpdateTimeoutOnView = 30 * 1000; // 30 seconds
// --------------------------------------------------------------------------------------------------
export class Board extends Component<BoardProp, BoardState>
{
    private autoUpdateInterval: number = -1;

    private toastId: ToastId = null;
    private toastCanceled: boolean = false;

    static whyDidYouRender = true;
    private serverAddress: string;
    private Theme: DashboardTheme;
    // --------------------------------------------------------------------------------------------------
    constructor(props: BoardProp) {
        super(props);

        let address = getGlobal("YodiBoardServerAddress");
        if (isDefined(address))
            this.serverAddress = address;
        else
            this.serverAddress = "";

        if (isDefined(this.props.Theme))
            this.Theme = this.props.Theme;
        else
            this.Theme = GetDefaultDashboardTheme();

        this.state = {
            isDashboardLoading: true,
            isDashboardSaving: false,
            isDashboardSavingFailed: false,
            selectedPageId: this._selectedPageId,
            LoadedYodiBoard: null,
            pages: {},
            widgetGroups: Factory.CreateWidgetGroup(),
            DialogModal: null,
            ErrorStatus: ErrorStatus.NoError,
            showCookiesConsent: false
        };
    }
    // ------------------------------------------------------
    private get _selectedPageId(): string {
        return !_.isEmpty(this.props.SelectedPageId)
            ? this.props.SelectedPageId
            : this.state?.LoadedYodiBoard?.Settings?.DefaultPageId ?? "";
    }
    // ------------------------------------------------------
    private get _onUnauthorizedRequest(): (loginPath: string) => void {
        return (loginPath: string): void => {
            if (isFunction(this.props.onUnauthorizedRequest))
                this.props.onUnauthorizedRequest(loginPath);
        };
    }
    // ------------------------------------------------------
    public ReloadYodiBoard() {
        // re-load the information from server
        if (isDefined(this.props.LoadingYodiBoardId))
            this.loadYodiBoard(this.props.LoadingYodiBoardId);
        else if (isDefined(this.props.LoadingPublicBoard))
            this.loadPublicYodiBoard(this.props.LoadingPublicBoard);
    }
    // ------------------------------------------------------
    private showCookiesConsent() {
        var cookies = localStorage.getItem(`cookies-${this.props.LoadingYodiBoardId}`);
        if (cookies == null)
            this.setState({ showCookiesConsent: true });
    }
    // ------------------------------------------------------
    componentDidMount() {
        // Start loading the information from server
        this.ReloadYodiBoard();

        // Show cookie consent if is not accepted
        this.showCookiesConsent();
    }
    // ----------------------------------------------------------------
    componentDidUpdate(prevProps: BoardProp) {
        if (prevProps.SelectedPageId != this.props.SelectedPageId &&
            this.state.selectedPageId != this.props.SelectedPageId &&
            isDefined(this.props.onSelectedBoardOrPageChange))
            this.setState({ selectedPageId: this.props.SelectedPageId });
    }
    // ------------------------------------------------------
    private showLoadingMessage(title: string) {
        /* Loading message */
        this.setState({
            DialogModal: {
                body: <div style={{ color: this.props.Theme.Loading.TextColor, padding: 50 }}>
                    <img
                        src={this.props.Theme.Loading.LoadingImageURL} alt="Loading"
                        style={{ display: 'block', margin: 'auto', width: '200px' }} />
                    <div style={{ padding: '8px 0', margin: 20 }} className="loader">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                    <h3 style={{ textAlign: 'center', fontWeight: 'bold' }}>{title}</h3>
                    {this.props.Theme.Loading.NoPoweredByText == true ? null :
                        <h4 style={{ textAlign: 'center', fontWeight: 'bold' }}>Powered by Yodiwo</h4>}
                </div>,
                actions: [<IconButton
                    key='cancel'
                    tooltip="Cancel"
                    iconClassName="fas fa-times"
                    iconStyle={{ color: "white" }}
                    onClick={() => this.setState({ DialogModal: null })} />]
            }
        });
    }
    // ------------------------------------------------------
    private initYodiBoardPages(webNode: WebNode, yodiBoard: YodiBoardExt) {
        this.showLoadingMessage("Connect WebNode");
        let inEditMode = this.props.IsEditable && yodiBoard.IsEditable;

        // Reform macros
        MacroSettingsUnpack(yodiBoard.Macros as MacroInfoEx[], inEditMode);

        // Init page localization
        try {
            let jsonLocalization: { [code: string]: LocalizationDictionary } = JSON.parse(yodiBoard.JsonLocalization);

            // Extract the default key 
            let defaultLocalizationKey = LocalizationDefaultKey;
            _.forOwn(jsonLocalization, (l, k) => {
                if (l.Info.IsDefault == true)
                    defaultLocalizationKey = k;
            });

            // Init localization instance
            let localization = Localization.GetInstance(webNode.NodeKey);
            if (!isDefined(localization)) {
                localization = new Localization(webNode.NodeKey, defaultLocalizationKey);
            }

            // update language dictionaries
            _.forOwn(jsonLocalization, (l, k) => {
                localization.UpdateTranslatedLanguage(k, l);
            });
        } catch (ex) {
            DebugEx.TraceException(ex);
            // If we don't have localization set a default one

            // Extract the default key 
            let defaultLocalizationKey = LocalizationDefaultKey;
            // Init localization instance
            let localization = Localization.GetInstance(webNode.NodeKey);
            if (!isDefined(localization))
                localization = new Localization(webNode.NodeKey, defaultLocalizationKey);
        }

        // Clean any old webthings
        webNode.RemoveAllModules();

        // Init webmodule for dashboard itself
        // We may not use the webmodule here but is creating the webmodule in webnode !!!!
        // DON'T DELETE IT....
        let webModule: WebModule = new WebModule(webNode, yodiBoard.WebModuleKey, "");

        // translate the boards
        let pages: DashboardDescriptorDictionary = {};
        _.forEach(yodiBoard.Pages, (page, key) => {
            try {
                let defaultSettings = DashboardHelper.getDefaultConfiguration();
                let settings: DashboardConfiguration;
                if (isStringEmpty(page.JsonDashboardSettings)) {
                    settings = defaultSettings;
                } else {
                    settings = JSON.parse(page.JsonDashboardSettings) as DashboardConfiguration;

                    // Migrate new configurations
                    if (!isDefined(settings.PageId))
                        settings.PageId = page.Id;
                }
                let viewWidgets = JSON.parse(page.JsonViewWidgets) as DashboardViewWidgets;

                // Old board init them with true the overrided
                if (settings.OverrideBorderRadius == null)
                    settings.OverrideBorderRadius = true;
                if ((settings.OverrideCanvasBackgroundColor as unknown as boolean) == true)
                    settings.OverrideCanvasBackgroundColor = BoardThemeColorOverride.StaticColor;
                if ((settings.OverrideWidgetBackgroundColor as unknown as boolean) == true)
                    settings.OverrideWidgetBackgroundColor = BoardThemeColorOverride.StaticColor;
                if (settings.OverrideZDepth == null)
                    settings.OverrideZDepth = true;

                // Set default values if we don't have one
                settings = _.merge({}, defaultSettings, settings);

                if (settings.CanvasBackgroundColor == "rgb(198, 211, 216)")
                    settings.CanvasBackgroundColor = defaultSettings.CanvasBackgroundColor;

                // The name of the web module node need to contain the hierarchy of the folders
                let WebModuleNaming = settings.Path + PlegmaStatics.Constants.HierarchySeparator + settings.Name;
                WebModuleNaming = WebModuleNaming.replace("//", "/");
                if (WebModuleNaming.startsWith(PlegmaStatics.Constants.HierarchySeparator))
                    WebModuleNaming = WebModuleNaming.substring(1);

                // Each dashboard is one separate web module
                let webModuleKey = page.WebModuleKey;
                let webModule: WebModule = new WebModule(webNode, webModuleKey, WebModuleNaming, settings.SessionBasedPortData);

                // Create the runtime widgets
                let widgets: IWidgetDictionary = {};
                _.forOwn(viewWidgets.WidgetsDesc, (wDesc, wKey) => {
                    if (wDesc.Key !== wKey) {
                        throw "Corrupted Dashboard";
                    }

                    // Set the default values to empty dictionary if we never set it
                    if (!isDefined(wDesc.DefaultValues)) {
                        wDesc.DefaultValues = {};
                    }
                    widgets[wKey] = Factory.CreateWidgetThing(wDesc, wDesc.Data, webModule);
                });

                pages[page.Id] = {
                    JsonViewWidgets: inEditMode ? page.JsonViewWidgets : null,
                    JsonDashboardSettings: inEditMode ? page.JsonDashboardSettings : null,
                    Key: page.Id,
                    ViewWidgets: viewWidgets,
                    Settings: settings,
                    WebModuleKey: page.WebModuleKey != null ? page.WebModuleKey : yodiBoard.WebModuleKey,
                    Widgets: widgets,
                    Timestamp: page.Timestamp
                };
            } catch (ex) {
                DebugEx.TraceException(ex);
            }
        });

        // Init BoardThings
        let boardThings = BoardThings.GetInstance(webNode.NodeKey);
        if (boardThings == null) {
            boardThings = new BoardThings(
                webNode.NodeKey,
                yodiBoard.WebModuleKey,
                {
                    changeSelectedPage: (key) => { DebugEx.TraceLog("BoardThing Event before init", key) },
                    openDialogDashboard: (popupData) => { DebugEx.TraceLog("BoardThing Event before init", popupData) },
                    closeDialogDashboard: () => { DebugEx.TraceLog("BoardThing Event before init", "") },
                    hidePages: (pages) => { DebugEx.TraceLog("BoardThing Event before init", pages) },
                    unHidePages: (pages) => { DebugEx.TraceLog("BoardThing Event before init", pages) },
                    refreshRequest: () => { },
                    openDrawerPage: (drawerData) => { DebugEx.TraceLog("BoardThing Event before init", drawerData) },
                    closeDrawerPage: (drawerData) => { DebugEx.TraceLog("BoardThing Event before init", drawerData) },
                    selectToolbarIcons: (toolbarIcons) => { DebugEx.TraceLog("selectToolbarIcons event before init", toolbarIcons) },
                    hideToolbarIcons: (toolbarIcons) => { DebugEx.TraceLog("hideToolbarIcons event before init", toolbarIcons) },
                    unhideToolbarIcons: (toolbarIcons) => { DebugEx.TraceLog("unhideToolbarIcons event before init", toolbarIcons) },
                    addLeftToolbarIcons: (toolbarIcons) => { DebugEx.TraceLog("addLeftToolbarIcons event before init", toolbarIcons) },
                    addRightToolbarIcons: (toolbarIcons) => { DebugEx.TraceLog("addRightToolbarIcons event before init", toolbarIcons) },
                    setToolbarIcons: (data) => { DebugEx.TraceLog("setToolbarIcons event before init", data) },
                    onLanguageChange: data => DebugEx.TraceLog('onLanguageChange event before init', data)
                },
                pages,
                yodiBoard.Settings.LocationService
            );
        } else {
            boardThings.ReInit(pages);
        }

        let boardThemeTemplate = null;
        if (!IsNullOrWhiteSpace(yodiBoard.JsonTheme))
            try { boardThemeTemplate = JSON.parse(yodiBoard.JsonTheme); }
            catch (ex) { DebugEx.TraceException(ex); }

        if (boardThemeTemplate == null)
            boardThemeTemplate = GetDefaultBoardTheme();

        // Set board theme
        let boardTheme = BoardTheming.GetInstance(webNode.NodeKey);
        if (boardTheme == null) {
            boardTheme = new BoardTheming(webNode.NodeKey, boardThemeTemplate)
        } else {
            boardTheme.SetTheme(boardThemeTemplate);
        }

        if (!webNode.DummyNode) {
            // Check if we need to update the server with any modified things
            let unitTime = DebugEx.Info.Timestamp.getTime() / 1000;
            if (webNode.YodiWS.ThingsRevNum < unitTime) {
                webNode.YodiWS.ThingsRevNum = unitTime;
                webNode.SendUpdateToServer();
            }
        }
        webNode.initDictionary();

        // Update UI with the loaded yodiboard
        this.setState({
            DialogModal: null,
            LoadedYodiBoard: yodiBoard,
            pages: pages,
            isDashboardLoading: false
        }, () => {
            if (this.state.selectedPageId != this._selectedPageId)
                this.setState({ selectedPageId: this._selectedPageId });

            if (isDefined(this.props.onPagesLoad))
                this.props.onPagesLoad(yodiBoard.Id, yodiBoard.Pages);
        });
    }
    // ------------------------------------------------------
    private initYodiBoard(yodiBoardOrig: DashboardStructs.YodiBoard, ServersInfo: { [key: number]: WebApps.CyanServerInfo }) {
        this.showLoadingMessage("Init Emerald");

        // update runtime settings; parse json
        let yodiBoard = YodiBoardToYodiboardExt(yodiBoardOrig);
        let webNode = WebNode.GetInstanceKey(yodiBoard.NodeKey);

        if (yodiBoard.Settings.WSPolicy == YodiBoardWSPolicy.NoWS) {
            if (webNode == null) {
                webNode = new WebNode({
                    dummyWS: true,
                    ServerUrl: "",
                    WebSocketUrl: "",
                    ApplicationDescription: yodiBoard.Description,
                    ApplicationImage: "",
                    ApplicationName: yodiBoard.Name,
                    ApplicationUUID: "",
                    Flags: Plegma.eConnectionFlags.None,
                    NodeType: Plegma.eNodeType.Dashboard,
                    NodeKey: yodiBoard.NodeKey,
                    BoardId: yodiBoard.Id,
                    Keys: {
                        nodeKey: yodiBoard.CyanNodeKey,
                        secretKey: yodiBoard.NodeSecret,
                        token1: null,
                        token2: null
                    },
                    UserInfo: this.props.UserInfo
                }, this.props.IsEditable && yodiBoard.IsEditable);

                webNode.DummyNode = true;

                // call `onConnected` so, we can call `onActivePortKeys` later
                webNode.YodiWS.onConnected(yodiBoard.NodeKey);
            } else {
                webNode.setInEditMode(this.props.IsEditable && yodiBoard.IsEditable);
            }
        } else {
            // Init the webnode if is not exist    
            if (webNode == null || !webNode.YodiWS.isPaired) {
                var url = ServersInfo[yodiBoard.CyanServer].URL;
                loadDomainURL(yodiBoard.CyanServer, url, (domain) => {
                    // If we don't have connection create one with the correct staffs
                    let conf: WSConf = {
                        dummyWS: false,
                        ApplicationDescription: yodiBoard.Description,
                        ApplicationImage: "",
                        ApplicationName: yodiBoard.Name,
                        ApplicationUUID: "e877280a-9f6d-11e6-80f5-76304dec7eb7",
                        Flags: Plegma.eConnectionFlags.CreateNewEndpoint,
                        NodeType: Plegma.eNodeType.Dashboard,
                        NodeKey: yodiBoard.NodeKey,
                        BoardId: yodiBoard.Id,
                        Keys: {
                            nodeKey: yodiBoard.CyanNodeKey,
                            secretKey: yodiBoard.NodeSecret,
                            token1: null,
                            token2: null
                        },
                        ServerUrl: domain.Cyan,
                        WebSocketUrl: domain.WebSocket,
                        AdditionalInfo: {
                            'controlpageuri': window.location.origin + window.location.pathname + '#/dashboard/0/' + yodiBoard.Id
                        },
                        UserInfo: this.props.UserInfo
                    };
                    webNode = new WebNode(conf, this.props.IsEditable && yodiBoard.IsEditable);

                    webNode.onStarted = (status) => {
                        if (yodiBoard.Settings.WSPolicy == YodiBoardWSPolicy.Legacy)
                            this.initYodiBoardPages(webNode, yodiBoard);
                    };

                    webNode.onError = (error, ecode, msg) => {
                        if (ecode == ErrorCode.InvalidKeys)
                            this.setState({ ErrorStatus: ErrorStatus.NoAccessRights });

                        if (isDefined(this.props.onError))
                            this.props.onError(msg);
                    };

                    webNode.onAssignedEndpoint = (key) => {
                        if (isDefined(this.props.onAssignedEndpoint))
                            this.props.onAssignedEndpoint(key);

                        // Create a global variable for assigned endpoint
                        setGlobal("AssignedEndpoint", key);
                    }

                    webNode.Start();
                });
            } else {
                webNode.setInEditMode(this.props.IsEditable && yodiBoard.IsEditable);
                if (yodiBoard.Settings.WSPolicy == YodiBoardWSPolicy.Legacy)
                    this.initYodiBoardPages(webNode, yodiBoard);
            }
        }

        // Don't wait websocket for loading
        if (yodiBoard.Settings.WSPolicy != YodiBoardWSPolicy.Legacy)
            this.initYodiBoardPages(webNode, yodiBoard);

        return yodiBoard;
    }
    // ------------------------------------------------------
    private async loadPublicYodiBoard(yodiBoard: DashboardStructs.YodiBoardPublicSharingInfo) {
        this.showLoadingMessage("Loading Emerald");

        // Load YodiBoard
        YFetch.request(ServerPaths["GetPublicBoard"].Method, {
            url: this.serverAddress + ServerPaths["GetPublicBoard"].Path,
            body: yodiBoard,
            onSuccess: (dataIn: DashboardStructs.YodiBoardGetPublicSharingResp) => {
                if (inDebug())
                    DebugEx.TraceLog("[POST] " + ServerPaths["GetPublicBoard"].Path, dataIn?.IsSuccess);

                if (dataIn?.IsSuccess) {
                    let board = this.initYodiBoard(dataIn.Board, dataIn.ServersInfo);
                    let selectedPageId = this.state.selectedPageId;

                    if (_.has(board.Settings, "DefaultPageId"))
                        selectedPageId = board.Settings.DefaultPageId;

                    if (isDefined(this.props.onSelectedBoardOrPageChange))
                        this.props.onSelectedBoardOrPageChange(yodiBoard.BoardId, selectedPageId);

                    this.setState({ selectedPageId: selectedPageId });
                } else {
                    this.setState({
                        DialogModal: null,
                        ErrorStatus: ErrorStatus.ServerProblem
                    });
                }
            },
            onFail: (textStatus: string) => {
                DebugEx.TraceError("Failed to load board", textStatus);
                this.setState({
                    DialogModal: null,
                    ErrorStatus: ErrorStatus.NetworkProblem
                });
                if (isDefined(this.props.onError))
                    this.props.onError("Failed to load Emerald");
            },
            onUnauthorize: this._onUnauthorizedRequest
        });
    }
    // ------------------------------------------------------
    private autoUpdateIntervalCb() {
        // If we have open toast don't show another one
        if (this.toastId != null)
            return;

        // Avoid auto update if we are in edit mode
        if (this.props.IsEditable)
            return;

        // get response
        YFetch.request(ServerPaths["GetYodiBoardVersions"].Method, {
            url: ServerPaths["GetYodiBoardVersions"].Path,
            body: {
                BoardId: this.state.LoadedYodiBoard?.Id
            },
            onSuccess: (dataIn: DashboardStructs.YodiBoardGetVersionsResp) => {
                if (dataIn.IsSuccess) {
                    if (dataIn.VersionHash != this.state.LoadedYodiBoard.VersionHash) {
                        const time = this.props.IsEditable ? UpdateTimeoutOnEdit : UpdateTimeoutOnView;
                        // show toastr
                        const render = (props: ToastContentProps) => <>
                            <div style={{ padding: 8 }}>
                                Detected application update.<br /> Auto reloading in {time / 1000} seconds.
                            </div>
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                <Button
                                    key='update'
                                    color='blue'
                                    onClick={() => props.closeToast()} >
                                    Update
                                </Button>
                                <Button
                                    key='cancel'
                                    onClick={() => {
                                        this.toastCanceled = true;
                                        props.closeToast();
                                    }}
                                    color='orange'>
                                    Cancel
                                </Button>
                            </div>
                        </>;

                        this.toastId = toast.warn(render, {
                            autoClose: time,
                            onClose: () => {
                                console.info("onClose, canceled: ", this.toastCanceled);
                                this.toastId = null;

                                if (!this.toastCanceled) {
                                    window.location.reload();
                                }

                                // reset helper
                                this.toastCanceled = false;
                            }
                        });
                    }
                }
                else {
                    console.error("Error getting pages' versions. Message: %O", dataIn.ErrorStr);
                }
            },
            onFail: (textStatus: string) => console.error(textStatus)
        });
    }
    // ------------------------------------------------------
    private async loadYodiBoard(id: string) {
        this.showLoadingMessage("Loading Board");

        try {
            var boardLoader = BoardLoader.GetInstance();
            var resp = await boardLoader.LoadBoard(id, this.props.IsEditable);
            if (inDebug())
                DebugEx.TraceLog("[POST] " + ServerPaths["LoadYodiBoards"].Path, resp?.IsSuccess);

            if (resp?.IsSuccess) {
                let board = this.initYodiBoard(resp.Board, this.props.ServersInfo);
                let selectedPageId = this.state.selectedPageId;

                if (_.has(board.Settings, "DefaultPageId"))
                    selectedPageId = board.Settings.DefaultPageId;

                if (isDefined(this.props.onSelectedBoardOrPageChange))
                    this.props.onSelectedBoardOrPageChange(id, selectedPageId);

                this.setState({ selectedPageId: selectedPageId });

                // initialize YodiBoard auto update if needed
                if (board.Settings.AutoUpdate) {
                    if (this.autoUpdateInterval === -1) {
                        const id = setInterval(() => { this.autoUpdateIntervalCb() }, AutoUpdateInterval);
                        this.autoUpdateInterval = Number(id);
                    }
                }
            } else {
                this.setState({
                    DialogModal: null,
                    ErrorStatus: ErrorStatus.ServerProblem
                });
            }
        } catch (ex) {
            DebugEx.TraceException(ex, "Failed to load board");
            this.setState({
                DialogModal: null,
                ErrorStatus: ErrorStatus.NetworkProblem
            });
            if (isDefined(this.props.onError))
                this.props.onError("Failed to load board");
        }
    }
    // ------------------------------------------------------
    private savePage(pages: DashboardDescriptor[], boardID: string) {
        // Translate the dashboards to yodiboard message
        let updatedPages: DashboardStructs.YodiPage[] = [];
        _.forEach(pages, (pageDesc) => {
            let desc = _.cloneDeep(pageDesc);

            // Extract only the data from widgets
            let widgetDesc: { [key: string]: IWidgetDescription } = {};
            _.forOwn(desc.Widgets, (w: IWidgetClass<any>, wkey) => {
                widgetDesc[wkey] = w.Description;
                widgetDesc[wkey].Key = wkey;
                widgetDesc[wkey].Data = w.WidgetData;
            });

            let _ViewWidgets = {
                Layout: isDefined(desc.ViewWidgets.Layout) ? desc.ViewWidgets.Layout : {},
                WidgetsDesc: widgetDesc,
                ReportPages: isDefined(desc.ViewWidgets.ReportPages) ? desc.ViewWidgets.ReportPages : {},
            };

            updatedPages.push({
                Id: desc.Key,
                Version: -1,
                Timestamp: desc.Timestamp,
                JsonDashboardSettings: JSON.stringify(desc.Settings),
                JsonViewWidgets: JSON.stringify(_ViewWidgets),
                WebModuleKey: desc.WebModuleKey,
                Deleted: false
            });
        });

        let req: DashboardStructs.YodiBoardUpdatePagesReq = {
            BoardId: boardID,
            DeletedPagesID: [],
            UpdatedPages: updatedPages,
            UndeletePages: [],
            JsonLocalization: null
        };

        // Extract localization data and send them for saving
        // TODO: create direct saving path from dashboard manager
        if (isDefined(this.state.LoadedYodiBoard)) {
            let localizationSystem = Localization.GetInstance(this.state.LoadedYodiBoard.NodeKey);
            if (localizationSystem != null && localizationSystem.PendingSaveChanges) {
                let localization = _.cloneDeep(localizationSystem.GetLanguages());
                // Don't save the default language ... part of the widgets settings.
                localization[localizationSystem.DefaultLanguage].Dictionary = {};
                req.JsonLocalization = JSON.stringify(localization);

                // TODO: check that the save was successfull
                localizationSystem.PendingSaveChanges = false;
            }
        }

        this.setState({ isDashboardSaving: true, isDashboardSavingFailed: false });

        // Send Post save request to server
        YFetch.request(ServerPaths["UpdatePages"].Method, {
            url: this.serverAddress + ServerPaths["UpdatePages"].Path,
            body: req,
            onSuccess: (dataIn: DashboardStructs.YodiBoardSaveResp) => {
                if (inDebug())
                    DebugEx.TraceLog("[POST] " + ServerPaths["UpdatePages"].Path, dataIn?.IsSuccess);

                // pass pages' versions to the parent
                if (dataIn?.IsSuccess) {
                    if (isDefined(this.props.onPagesLoad))
                        this.props.onPagesLoad(this.state.LoadedYodiBoard.Id, dataIn.Pages);
                } else {
                    this.setState({ isDashboardSavingFailed: true });
                    this.showToastError("Failed to save the board");
                }
            },
            onFail: (textStatus: string) => {
                try { throw new Error("Failed to save page: " + textStatus); }
                catch (localErr) { DebugEx.TraceCriticalException(localErr, "Failed to save page", textStatus); }
                this.setState({ isDashboardSavingFailed: true });
                this.showToastError("Failed to save the page");
            },
            onComplete: () => this.setState({ isDashboardSaving: false }),
            onUnauthorize: this._onUnauthorizedRequest
        });
    }
    // ------------------------------------------------------
    private deletePage(pageId: string, boardID: string) {
        let req: DashboardStructs.YodiBoardUpdatePagesReq = {
            BoardId: boardID,
            DeletedPagesID: [pageId],
            UpdatedPages: [],
            UndeletePages: [],
            JsonLocalization: null
        };

        this.setState({ isDashboardSaving: true, isDashboardSavingFailed: false });

        // Send Post save request to server
        YFetch.request(ServerPaths["UpdatePages"].Method, {
            url: this.serverAddress + ServerPaths["UpdatePages"].Path,
            body: req,
            onSuccess: (dataIn: DashboardStructs.YodiBoardSaveResp) => {
                if (inDebug())
                    DebugEx.TraceLog("[POST] " + ServerPaths["UpdatePages"].Path, dataIn?.IsSuccess);

                // pass pages' versions to the parent
                if (dataIn?.IsSuccess) {
                    if (isDefined(this.props.onPagesLoad))
                        this.props.onPagesLoad(this.state.LoadedYodiBoard.Id, dataIn.Pages);
                } else {
                    this.setState({ isDashboardSavingFailed: true });
                    this.showToastError("Failed to delete page from the board");
                }
            },
            onFail: (textStatus: string) => {
                try { throw new Error("Failed to delete page: " + textStatus); }
                catch (localErr) { DebugEx.TraceCriticalException(localErr, "Failed to save page", textStatus); }
                this.setState({ isDashboardSavingFailed: true });
                this.showToastError("Failed to delete the page");
            },
            onComplete: () => this.setState({ isDashboardSaving: false }),
            onUnauthorize: this._onUnauthorizedRequest
        });
    }
    // ------------------------------------------------------
    private onPageSave = (pages: DashboardDescriptor[], updateExternalStorage: boolean) => {
        this.setState((s: BoardState) => {
            // Update all pages 
            _.forEach(pages, (page) => {
                // Update widgets sup elements but keep the entry level
                let deletedWidgets = [];
                _.forOwn(s.pages[page.Key].Widgets, (w) => {
                    if (_.has(page.Widgets, w.Description.Key)) {
                        let wNew = page.Widgets[w.Description.Key];
                        _.forOwn(w, (d, k) => {
                            if (_.has(wNew, k)) {
                                w[k] = wNew[k];
                            }
                        });
                    } else {
                        // Delete w !!!
                        deletedWidgets.push(w.Description.Key);
                    }
                });
                _.forEach(deletedWidgets, wKey => _.unset(s.pages[page.Key].Widgets, wKey));
                _.forOwn(page.Widgets, (w) => {
                    if (!_.has(s.pages[page.Key].Widgets, w.Description.Key)) {
                        s.pages[page.Key].Widgets[w.Description.Key] = w;
                    }
                });

                s.pages[page.Key].JsonDashboardSettings = _.cloneDeep(page.JsonDashboardSettings);
                s.pages[page.Key].JsonViewWidgets = _.cloneDeep(page.JsonViewWidgets);
                s.pages[page.Key].Settings = _.cloneDeep(page.Settings);
                s.pages[page.Key].Timestamp = _.cloneDeep(page.Timestamp);
                s.pages[page.Key].ViewWidgets = _.cloneDeep(page.ViewWidgets);
                s.pages[page.Key].WebModuleKey = _.cloneDeep(page.WebModuleKey);
            });
            return s;
        }, () => {
            if (updateExternalStorage && isDefined(this.state.LoadedYodiBoard))
                this.savePage(_.map(pages, (page) => this.state.pages[page.Key]), this.state.LoadedYodiBoard.Id);
        });
    }
    // ------------------------------------------------------
    private onPageAdd = (page: DashboardDescriptor, updateExternalStorage: boolean) => {
        this.setState((state) => {
            state.pages[page.Key] = _.cloneDeep(page);
            return state;
        }, () => {
            if (updateExternalStorage && isDefined(this.state.LoadedYodiBoard))
                this.savePage([this.state.pages[page.Key]], this.state.LoadedYodiBoard.Id);
        });
    }
    // ------------------------------------------------------
    private onPageDelete = (key: string, updateExternalStorage: boolean) => {
        this.setState((state) => {
            delete state.pages[key];
            return state;
        }, () => {
            if (updateExternalStorage && isDefined(this.state.LoadedYodiBoard))
                this.deletePage(key, this.state.LoadedYodiBoard.Id);
        });
    }
    // ------------------------------------------------------
    private renderFailedToLoad(errorStatus: ErrorStatus) {
        return <div style={{ width: this.props.width, height: this.props.height }}>
            <div
                key={"paper-loading"}
                className="noselect"
                style={{
                    boxShadow: getZDepthShadow(2),
                    width: 400,
                    height: 200,
                    textAlign: 'center',
                    margin: '20px auto',
                    fontSize: 'x-large',
                    fontWeight: 700,
                    borderRadius: 10,
                }}>
                <div style={{
                    width: 120,
                    height: 120,
                    padding: 30,
                    margin: "0 auto"
                }}>
                    <i style={{ fontSize: "60px" }} className="fas fa-exclamation-triangle"></i>
                </div>
                <p>
                    {(() => {
                        switch (errorStatus) {
                            case ErrorStatus.NetworkProblem:
                                return "Network problems";
                            case ErrorStatus.NoAccessRights:
                                return "No authorized access";
                            case ErrorStatus.ServerProblem:
                                return "Sorry we couldn't load the board";
                        }
                    })()}
                </p>
            </div>
        </div>;
    }
    // ------------------------------------------------------
    private renderDashboard() {
        if (this.state.ErrorStatus != ErrorStatus.NoError)
            return this.renderFailedToLoad(this.state.ErrorStatus);

        const board = this.state.LoadedYodiBoard;
        if (!isDefined(board) && !this.state.isDashboardLoading)
            return this.renderFailedToLoad(ErrorStatus.ServerProblem);

        if (!isDefined(board))
            return null;

        let webNode = WebNode.GetInstanceKey(board.NodeKey);
        if (webNode == null)
            return this.renderFailedToLoad(ErrorStatus.NetworkProblem);

        return (
            <div id={board.Id} style={{ overflow: "hidden" }}>
                <DashboardManager
                    nodeKey={board.NodeKey}
                    width={this.props.width}
                    height={this.props.height}
                    dashboards={this.state.pages}
                    board={board}
                    isPublicDomain={this.props.UserInfo == null}
                    widgetGroups={this.state.widgetGroups}
                    isLoading={this.state.isDashboardLoading}
                    isSavingFailed={this.state.isDashboardSavingFailed}
                    isSaving={this.state.isDashboardSaving}
                    onPageSave={this.onPageSave}
                    onPageAdd={this.onPageAdd}
                    onPageDelete={this.onPageDelete}
                    supportEditing={this.props.IsEditable && board.IsEditable}
                    Theme={this.Theme}
                    selectedDashboardKey={this.state.selectedPageId}
                    onSelectedDashboardKeyChange={(key) => {
                        this.setState({ selectedPageId: key });
                        if (isDefined(this.props.onSelectedBoardOrPageChange))
                            this.props.onSelectedBoardOrPageChange(board.Id, key);
                    }}
                    onDefaultPageIdChange={this._handleDefaultPageIdChange}
                    onToolbarIconsChange={this._handleToolbarIconsChange}
                    DefaultIsListOpen={this.props.DefaultIsListOpen}
                    DefaultIsListEnabled={this.props.DefaultIsListEnabled}
                    onMacroUpdate={(newMacros, onMacroSaveSuccess) => {
                        let request: DashboardStructs.YodiBoardUpdateMacroReq = {
                            BoardId: board.Id,
                            DeleteMacro: [],
                            UpdateMacro: []
                        };

                        // Find the deleted
                        _.forEach(board.Macros, m => {
                            let nm = _.find(newMacros, nm => nm.Guid == m.Guid);
                            if (!isDefined(nm))
                                request.DeleteMacro.push(m);
                        });

                        // Find the Updated/News
                        _.forEach(newMacros, nm => {
                            let m = _.find(board.Macros, m => m.Guid == nm.Guid);
                            if (isDefined(m)) {
                                if (!_.isEqual(nm, m)) {
                                    //Updated
                                    request.UpdateMacro.push(nm);
                                }
                            } else {
                                // New
                                request.UpdateMacro.push(nm);
                            }
                        });

                        // Send the request to server
                        if (request.DeleteMacro.length > 0 ||
                            request.UpdateMacro.length > 0)
                            YFetch.request(ServerPaths["SaveYodiBoardMacros"].Method, {
                                url: ServerPaths["SaveYodiBoardMacros"].Path,
                                body: request,
                                onSuccess: (dataIn: DashboardStructs.YodiBoardUpdateMacroResp) => {
                                    // Update component's state.
                                    this.setState((s: BoardState) => {
                                        if (!dataIn.IsSuccess)
                                            this.showToastError(dataIn.ErrorStr);
                                        else
                                            s.LoadedYodiBoard.Macros = newMacros;
                                        return s;
                                    }, () => {
                                        if (!dataIn.IsSuccess)
                                            onMacroSaveSuccess(false, dataIn.ErrorStr);
                                        else
                                            onMacroSaveSuccess(true, "");
                                    });
                                },
                                onFail: (textStatus: string) => {
                                    // Request failed.
                                    console.error(textStatus)
                                    onMacroSaveSuccess(false, textStatus);
                                }
                            });
                        else
                            onMacroSaveSuccess(true, "");
                    }}
                    getToolbarElements={() => {
                        let elements = null;

                        if (isFunction(this.props.getToolbarElements))
                            elements = this.props.getToolbarElements(board.NodeKey)
                        return {
                            LeftElements: elements?.LeftElements ?? [],
                            CenterElements: elements?.CenterElements ?? [],
                            RightElements: [
                                board.Settings?.HideWSConnection ? null :
                                    <WSStatus
                                        key="wsstatus"
                                        NodeKey={board.NodeKey}
                                    />,
                                ...(elements?.RightElements ?? [])
                            ]
                        }
                    }}
                />
            </div>
        );
    }
    // ------------------------------------------------------
    private _handleToolbarIconsChange = (icons: ToolbarIconInfo[]) => {
        this._saveBoardSettings({ ToolbarIcons: icons, } as YodiBoardConfiguration,
            (dataIn: DashboardStructs.YodiBoardUpdateConfResp) => {
                // Update component's state.
                this.setState((s: BoardState) => {
                    if (!dataIn.IsSuccess)
                        this.showToastError(dataIn.ErrorStr);
                    else
                        s.LoadedYodiBoard.Settings.ToolbarIcons = icons;
                    return s;
                });
            });
    }
    // ------------------------------------------------------
    private _handleDefaultPageIdChange = (defaultPageId: string) => {
        this._saveBoardSettings({ DefaultPageId: defaultPageId } as YodiBoardConfiguration,
            (dataIn: DashboardStructs.YodiBoardUpdateConfResp) => {
                // Update component's state.
                this.setState((s: BoardState) => {
                    if (!dataIn.IsSuccess)
                        this.showToastError(dataIn.ErrorStr);
                    else
                        s.LoadedYodiBoard.Settings.DefaultPageId = defaultPageId;
                    return s;
                });
            });
    }
    // ------------------------------------------------------
    private _saveBoardSettings = (
        settings: YodiBoardConfiguration,
        successCB: (dataIn: DashboardStructs.YodiBoardUpdateConfResp) => void) => {

        // Perform HTTP request for updating board's configuration.
        YFetch.request(ServerPaths["UpdateYodiBoardConf"].Method, {
            url: ServerPaths["UpdateYodiBoardConf"].Path,
            body: _.assign({}, this.state.LoadedYodiBoard, {
                // Merge settings and convert to JSON string. The default 
                // method is used because the merge one ignores the arrays.
                JsonSettings: JSON.stringify(_.defaults({},
                    settings,
                    this.state.LoadedYodiBoard.Settings
                ))
            }) as DashboardStructs.YodiBoard,
            onSuccess: (dataIn: DashboardStructs.YodiBoardUpdateConfResp) => RunSafe(() => successCB(dataIn)),
            onFail: (textStatus: string) => console.error(textStatus),
            onUnauthorize: this._onUnauthorizedRequest
        });
    }
    // ------------------------------------------------------
    private get _maintenance(): DashboardMaintenance {
        return this.state.LoadedYodiBoard.Settings.Maintenance;
    }
    // ------------------------------------------------------
    private get _inMaintenance(): boolean {
        // Are we in maintenance?
        const dashboard = this.state.LoadedYodiBoard;
        return isDefined(dashboard) && this._maintenance.IsEnabled && !this.props.IsEditable;
    }
    // ------------------------------------------------------
    userAcceptedCookiesConsent = () => {
        localStorage.setItem(`cookies-${this.props.LoadingYodiBoardId}`, "accepted");
        this.setState({ showCookiesConsent: false });
    }
    // ------------------------------------------------------
    renderCookiesConsent() {
        if (!this.state.showCookiesConsent ||
            this.props.DomainInfo == null ||
            this.props.DomainInfo.CookiesJSON == null)
            return null;

        var info: WebApps.CookiesConsentInfo = null;
        try {
            info = JSON.parse(this.props.DomainInfo.CookiesJSON);
        } catch (ex) {
            DebugEx.TraceException(ex);
        }

        // if we don't have info we hide the cookie consent
        if (info == null)
            return null;

        return <div className="cookieConsentContainer" style={{ display: "block", opacity: 1 }}>
            <div className="cookieTitle"><h3>Cookies</h3></div>
            <div className="cookieDesc">
                <div style={{ padding: 5 }} dangerouslySetInnerHTML={{ __html: info.description }} />
            </div>
            <div className="cookieDesc">
                <Panel
                    header={<h4 style={{ color: "white" }}>More Info</h4>}
                    collapsible bordered shaded bodyFill
                >
                    <div style={{ padding: 5 }} dangerouslySetInnerHTML={{ __html: info.moreInfo }} />
                </Panel>
            </div>
            <div className="cookieButton"><a onClick={this.userAcceptedCookiesConsent}>Understood</a></div>
        </div>
    }
    // ------------------------------------------------------
    showToastError = (message: string) => showToastNotification({
        Type: 'error',
        Position: 'bottom-center',
        Title: null,
        Icon: 'fa fa-2x fa-exclamation-circle',
        Body: message,
        AdvanceBody: null,
        Autoclose: 3000,
        CloseButton: true,
        HideProgressBar: true
    }, undefined);
    // ------------------------------------------------------
    render() {
        return this._inMaintenance
            ? <Suspense fallback={<LazyLoadingAnimation />}>
                <Maintenance
                    width={this.props.width}
                    height={this.props.height}
                    image={this._maintenance.Image}
                    primaryMsg={this._maintenance.PrimaryMsg}
                    secondaryMsg={this._maintenance.SecondaryMsg} />
            </Suspense>
            : <Fragment>
                {this.renderDashboard()}
                <ExDialog Enable={isDefined(this.state.DialogModal)} Conf={this.state.DialogModal} />
                {this.renderCookiesConsent()}
            </Fragment>
    }
}
// ------------------------------------------------------
export default Board;